/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

interface ParsedVersion {
  major: number;
  minor: number;
  patch: number;
  prerelease: string[];
}

const SEMVER_PATTERN =
  /^(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z-.]+))?(?:\+[0-9A-Za-z-.]+)?$/;

/**
 * Strips the decorations registries and release tooling add around a version,
 * so `v1.0.0-dev.2 ` and `1.0.0-dev.2` compare as the same version.
 */
export const normalizeVersion = (version: string): string =>
  version.trim().replace(/^[v=]+/, '');

export const parseVersion = (version: string): ParsedVersion | null => {
  const match = SEMVER_PATTERN.exec(normalizeVersion(version));

  if (!match) {
    return null;
  }

  const [, major, minor, patch, prerelease] = match;

  return {
    major: Number(major),
    minor: Number(minor),
    patch: Number(patch),
    prerelease: prerelease ? prerelease.split('.') : [],
  };
};

const isNumericIdentifier = (identifier: string): boolean =>
  /^\d+$/.test(identifier);

/** Compares a single pair of prerelease identifiers: `2` vs `10`, `dev` vs `rc`. */
const compareIdentifier = (left: string, right: string): number => {
  const leftIsNumeric = isNumericIdentifier(left);
  const rightIsNumeric = isNumericIdentifier(right);

  if (leftIsNumeric && rightIsNumeric) {
    return Number(left) - Number(right);
  }

  // Numeric identifiers rank below alphanumeric ones.
  if (leftIsNumeric !== rightIsNumeric) {
    return leftIsNumeric ? -1 : 1;
  }

  if (left === right) {
    return 0;
  }

  return left < right ? -1 : 1;
};

/**
 * Compares two prerelease identifier lists by semantic versioning.
 */
const comparePrerelease = (left: string[], right: string[]): number => {
  if (left.length === 0 || right.length === 0) {
    return right.length - left.length;
  }

  const sharedLength = Math.min(left.length, right.length);

  for (let index = 0; index < sharedLength; index += 1) {
    const difference = compareIdentifier(left[index], right[index]);

    if (difference !== 0) {
      return difference;
    }
  }

  // All shared identifiers match: the longer list is the later prerelease.
  return left.length - right.length;
};

export const compareVersions = (versionA: string, versionB: string): number => {
  const parsedA = parseVersion(versionA);
  const parsedB = parseVersion(versionB);

  if (!parsedA || !parsedB) {
    if (parsedA) {
      return 1;
    }
    if (parsedB) {
      return -1;
    }
    return versionA.localeCompare(versionB);
  }

  return (
    parsedA.major - parsedB.major ||
    parsedA.minor - parsedB.minor ||
    parsedA.patch - parsedB.patch ||
    comparePrerelease(parsedA.prerelease, parsedB.prerelease)
  );
};

/** Newest version first, the order the version menu lists them in. */
export const sortVersionsDescending = (versions: string[]): string[] =>
  [...versions].sort((versionA, versionB) =>
    compareVersions(versionB, versionA),
  );

export const getHighestVersion = (versions: string[]): string | undefined =>
  sortVersionsDescending(versions)[0];
